// SEO-12 · /resources/school-certificate-a-third-party-can-verify
//
// EDITORIAL GROUNDING (see docs/marketing/RESOURCES_EDITORIAL_PROCESS.md):
//   • Claims registry D5, verbatim in substance: serials come from the school's OWN register;
//     holder identity is FROZEN at issue; a revoked certificate verifies as invalid; a no-dues
//     certificate is blocked by outstanding fees unless an authorised override is RECORDED; the
//     public verification page shows only the printed fields.
//   • CLAUDE.md: typed certificate register, branded public `/verify/:slug/:serial` page reached
//     by QR, bulk issue, the `students.fee_blocked` gate returning 423 with a recorded
//     `override_fee_block`, and `core/id_cards.py` printing only printed fields on its QR target.
//   • Sequential numbers come from the atomic COUNTERS register, never count(*)+1 — CLAUDE.md
//     "Never-ship rails" + `db/services/numbering.allocate_sequence`.
//   • Registry boundary: "Never suggest the platform attests to facts the school has not recorded."
// NO invented verification volumes, no legal-validity claim, no government-registry claim.

const article = {
  slug: "school-certificate-a-third-party-can-verify",
  title: "A certificate a third party can actually verify",
  seoTitle: "Verifiable School Certificates | Vidyapeeth360",
  description:
    "Serial numbers, frozen identity, revocation and a public check — what turns a school certificate from a printed PDF into something a stranger can trust.",
  topic: "Compliance",
  topicKey: "compliance",
  readingMinutes: 6,
  published: "2026-08-18",
  updated: "2026-08-18",
  standfirst:
    "A transfer certificate is only useful to the school that receives it. Which means the question that matters is not how good it looks, but what a stranger can do to check that your school really issued it.",
  moduleLinks: ["certificate-generation", "id-card-management", "document-management"],
  conversion: {
    to: "/pricing",
    label: "See plans and the full cost schedule",
    note: "Certificate issuance and the public verification page are included in every current plan, not sold as a separate add-on.",
  },
  sections: [
    {
      id: "the-forgery-problem",
      heading: "The document is not the evidence",
      blocks: [
        {
          t: "p",
          text: "A transfer certificate, a bonafide letter and a character certificate all share a design flaw: they are pieces of paper that assert something, carrying a signature the receiving school has never seen before. That school's actual verification procedure is usually to phone the issuing school and hope somebody answers.",
        },
        {
          t: "p",
          text: "That is not a paperwork problem to solve with a nicer template. It is an evidence problem, and it has a short list of requirements.",
        },
      ],
    },
    {
      id: "requirements",
      heading: "Five properties that make a certificate checkable",
      blocks: [
        {
          t: "ol",
          items: [
            "**A serial from one register.** Every certificate the school issues takes the next number from a single running series. Not per clerk, not per type restarting each year, not derived by counting existing rows — a count breaks the moment one is cancelled or two are issued at once, and duplicate serials make the whole register worthless.",
            "**Identity frozen at issue.** The name, class, admission number and dates printed on the certificate are what was true on the day it was issued. If the underlying record is corrected next year, the issued certificate must not silently change — otherwise a verification in 2028 contradicts the paper in the applicant's hand.",
            "**A public check that needs no account.** The person verifying is a stranger: another school's clerk, an employer, a university office. They will not create a login. A QR or a serial they can type has to be enough.",
            "**Revocation that shows.** A certificate issued in error and cancelled must verify as *invalid*, not fail to load. Silence is indistinguishable from a broken link, and a determined holder will keep presenting the PDF.",
            "**Minimal disclosure.** The verification page confirms the printed fields and nothing else. Whoever holds the serial should learn no more about the child than the document already tells them — no address, no guardian phone, no fee history.",
          ],
        },
      ],
    },
    {
      id: "the-no-dues-trap",
      heading: "The no-dues certificate, and how it goes wrong",
      blocks: [
        {
          t: "p",
          text: "A no-dues certificate is different from every other type, because it is a financial assertion. Issued carelessly it becomes the document a family uses to close a balance the school never actually collected.",
        },
        {
          t: "p",
          text: "So the fee position should be checked by the system at issue rather than by the memory of whoever is at the counter. When there are dues, issuance should stop. Not warn — stop.",
        },
        {
          t: "note",
          title: "But keep an override, and record it",
          text: "Real schools issue a no-dues certificate over an outstanding balance sometimes: a waiver in progress, a hardship decision, a family in a genuine emergency. A block with no override forces the office to route around the system entirely, which is worse. The right design is a block that an authorised person can override, with the override recorded against the certificate — so the exception is visible, attributable, and rare.",
        },
      ],
    },
    {
      id: "bulk",
      heading: "Issuing a hundred at once without losing the register",
      blocks: [
        {
          t: "p",
          text: "End of year, an entire outgoing batch needs certificates. Two things go wrong at that scale, and both are avoidable.",
        },
        {
          t: "ul",
          items: [
            "**Serial collisions under concurrency.** Two operators issuing simultaneously, or one retry that fires twice, can hand out the same number if the series is computed by counting. It has to come from an atomic allocation that never returns the same value twice.",
            "**Silent partial failure.** A batch that stops halfway leaves a register that half-matches reality. You want either a batch that completes, or a clear list of who did not get one — never a total that looks right and a set of files that does not.",
          ],
        },
      ],
    },
    {
      id: "in-the-product",
      heading: "How this works in Vidyapeeth360",
      blocks: [
        {
          t: "p",
          text: "Certificates are typed — transfer, bonafide, character, no-dues, income, promotion and staff experience among them — and every issue takes its serial from the school's own sequential register, allocated atomically so a retry or a second operator can never mint a duplicate. The holder's identity is snapshotted onto the certificate at issue, so a later correction to the student record does not rewrite a document already in somebody's hand.",
        },
        {
          t: "p",
          text: "Each certificate carries a QR to a branded public verification page keyed by the school and the serial. Anyone can open it without an account, and it shows the printed fields only — issue date, type, holder name and status — which is enough to confirm the paper and not enough to learn anything the paper does not already say. A revoked certificate verifies as invalid rather than disappearing.",
        },
        {
          t: "p",
          text: "The no-dues gate is enforced at issue: a student flagged for outstanding fees is refused, including in a bulk run, and an authorised staff member can override it deliberately — with the override recorded rather than assumed. That is the same maker-checker instinct the rest of the money workflows use.",
        },
        {
          t: "p",
          text: "The register itself is exportable, so \"what did we issue last year\" is a report rather than an archaeology project. Student [ID cards](/modules/id-card-management) use the same printed-fields-only principle for their public check, and issued PDFs land in the student's [document record](/modules/document-management) alongside everything else the school holds for them.",
        },
        {
          t: "p",
          text: "One boundary to be clear about: verification confirms that your school issued this certificate with these printed details. It does not attest to facts the school never recorded, and it is not a government registry. That limit is the reason the check is trustworthy — it makes exactly one claim, and that claim is one the school can stand behind.",
        },
      ],
    },
  ],
  sources: [],
};

export default article;
